/**
 * Top-level Tasks tab shell — owns view/mode/filter state, fetches all
 * supporting data (tasks, summary counts, label lookups), and orchestrates
 * the toolbar + table/board + drawer.
 */
import { ClipboardList } from "lucide-react";
import { useMemo, useState } from "react";
import { ErrorState } from "@/components/shared/error-state";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { useTradeCategories } from "@/hooks/use-config";
import { useContacts } from "@/hooks/use-contacts";
import {
  type ProjectTask,
  type TaskSummaryCounts,
  useCreateTasksFromTemplate,
  useProjectTasks,
  useTaskSummaryCounts,
  useTaskTemplates,
} from "@/hooks/use-tasks";
import "./task-tokens.css";
import { authClient } from "@/lib/auth-client";
import { NewTaskDialog } from "./NewTaskDialog";
import { TaskDrawer } from "./TaskDrawer";
import { TasksBoard } from "./TasksBoard";
import { TasksTable } from "./TasksTable";
import {
  type SavedViewCounts,
  type SavedViewId,
  TasksToolbar,
  type ViewMode,
} from "./TasksToolbar";

function isThisWeek(dateIso: string | null): boolean {
  if (!dateIso) return false;
  const d = new Date(dateIso);
  if (Number.isNaN(d.getTime())) return false;
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const in7 = new Date(now);
  in7.setDate(in7.getDate() + 7);
  return d >= now && d <= in7;
}

function isOverdue(dateIso: string | null, status: string | null): boolean {
  if (!dateIso) return false;
  if (status === "completed") return false;
  const d = new Date(dateIso);
  if (Number.isNaN(d.getTime())) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return d < today;
}

function filterBySavedView(
  tasks: ProjectTask[],
  view: SavedViewId,
  currentUserId: string | null,
): ProjectTask[] {
  switch (view) {
    case "all":
      return tasks;
    case "mine":
      return tasks.filter((t) => currentUserId && t.assignedToUserId === currentUserId);
    case "week":
      return tasks.filter((t) => isThisWeek(t.dueDate));
    case "blocked":
      return tasks.filter((t) => t.status === "blocked");
    case "overdue":
      return tasks.filter((t) => isOverdue(t.dueDate, t.status));
  }
}

function TaskFromTemplateDialog({
  projectId,
  open,
  onOpenChange,
}: {
  projectId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { data: templates, isLoading } = useTaskTemplates();
  const generateTasks = useCreateTasksFromTemplate(projectId);
  const [templateId, setTemplateId] = useState("_none");

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (templateId === "_none") return;
    generateTasks.mutate(
      { templateId },
      {
        onSuccess: () => {
          onOpenChange(false);
          setTemplateId("_none");
        },
      },
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold text-[#334155]">
            Generate Tasks from Template
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="task-tmpl-select" className="text-sm font-medium text-gray-700">
              Template
            </Label>
            <Select value={templateId} onValueChange={setTemplateId}>
              <SelectTrigger id="task-tmpl-select">
                <SelectValue placeholder="Select a template..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="_none" disabled>
                  Select a template…
                </SelectItem>
                {isLoading ? (
                  <SelectItem value="_loading" disabled>
                    Loading…
                  </SelectItem>
                ) : (templates ?? []).length === 0 ? (
                  <SelectItem value="_empty" disabled>
                    No templates available
                  </SelectItem>
                ) : (
                  (templates ?? []).map((t) => (
                    <SelectItem key={t.id} value={t.id}>
                      {t.name}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={templateId === "_none" || generateTasks.isPending}
              className="bg-[#334155] hover:bg-[#415A77] text-white"
            >
              {generateTasks.isPending ? "Generating..." : "Generate"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function TasksTab({ projectId }: { projectId: string }) {
  const { data: tasks, isLoading, isError, error, refetch } = useProjectTasks(projectId);
  const { data: summaryCounts } = useTaskSummaryCounts(projectId);
  const { data: trades } = useTradeCategories();
  const { data: contacts } = useContacts();
  const { data: session } = authClient.useSession();
  const currentUserId = session?.user?.id ?? null;

  const [view, setView] = useState<SavedViewId>("all");
  const [mode, setMode] = useState<ViewMode>("table");
  const [drawerTask, setDrawerTask] = useState<ProjectTask | null>(null);
  const [newTaskOpen, setNewTaskOpen] = useState(false);
  const [templateOpen, setTemplateOpen] = useState(false);

  const assigneeLabels = useMemo(() => {
    const m = new Map<string, string>();
    for (const t of trades ?? []) m.set(t.id, t.name);
    for (const c of contacts ?? []) m.set(c.id, `${c.firstName ?? ""} ${c.lastName ?? ""}`.trim());
    return m;
  }, [trades, contacts]);

  const summaryMap = useMemo(() => {
    const m = new Map<string, TaskSummaryCounts>();
    for (const row of summaryCounts ?? []) m.set(row.task_id, row);
    return m;
  }, [summaryCounts]);

  const allTasks = tasks ?? [];

  // Keep drawerTask in sync with the latest fetched data so mutations (e.g.
  // selecting an evidence folder) are immediately reflected in the drawer.
  const liveDrawerTask = drawerTask
    ? (allTasks.find((t) => t.id === drawerTask.id) ?? drawerTask)
    : null;

  const savedViewCounts = useMemo<SavedViewCounts>(
    () => ({
      all: allTasks.length,
      mine: filterBySavedView(allTasks, "mine", currentUserId).length,
      week: filterBySavedView(allTasks, "week", currentUserId).length,
      blocked: filterBySavedView(allTasks, "blocked", currentUserId).length,
      overdue: filterBySavedView(allTasks, "overdue", currentUserId).length,
    }),
    [allTasks, currentUserId],
  );

  const visibleTasks = useMemo(
    () => filterBySavedView(allTasks, view, currentUserId),
    [allTasks, view, currentUserId],
  );

  if (isLoading) return <Skeleton className="h-64 w-full" />;
  if (isError) return <ErrorState message={error?.message} onRetry={() => refetch()} />;

  return (
    <div className="pbos-tasks space-y-3">
      <TasksToolbar
        view={view}
        onViewChange={setView}
        mode={mode}
        onModeChange={setMode}
        counts={savedViewCounts}
        onNewTask={() => setNewTaskOpen(true)}
        onGenerateFromTemplate={() => setTemplateOpen(true)}
      />

      {allTasks.length === 0 ? (
        <div className="rounded-lg border border-[var(--border-1)] bg-[var(--paper-0)] p-12 text-center">
          <ClipboardList className="mx-auto h-10 w-10 text-[var(--text-4)]" />
          <p className="mt-2 text-[13px] text-[var(--text-3)]">No tasks yet.</p>
        </div>
      ) : mode === "table" ? (
        <TasksTable
          tasks={visibleTasks}
          projectId={projectId}
          summaryCounts={summaryMap}
          assigneeLabels={assigneeLabels}
          onOpenTask={setDrawerTask}
        />
      ) : (
        <TasksBoard
          tasks={visibleTasks}
          assigneeLabels={assigneeLabels}
          onOpenTask={setDrawerTask}
        />
      )}

      <NewTaskDialog
        projectId={projectId}
        open={newTaskOpen}
        onOpenChange={setNewTaskOpen}
        trades={trades ?? []}
        onCreated={(task) => {
          setNewTaskOpen(false);
          setDrawerTask(task);
        }}
      />
      <TaskFromTemplateDialog
        projectId={projectId}
        open={templateOpen}
        onOpenChange={setTemplateOpen}
      />

      <TaskDrawer
        projectId={projectId}
        task={liveDrawerTask}
        open={!!drawerTask}
        onOpenChange={(open) => {
          if (!open) setDrawerTask(null);
        }}
        allTasks={allTasks}
        trades={trades ?? []}
      />
    </div>
  );
}
